import React, { useEffect, useRef } from 'react';

// An illustrative network, independent of the empirical proof-system data.
export const HeroDependencyField = ({ mode }) => {
  const canvasRef = useRef(null);
  const modeRef = useRef(mode);
  const drawRef = useRef(null);
  useEffect(() => { modeRef.current = mode; drawRef.current?.(); }, [mode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame, width = 0, height = 0, blend = 0, visible = true, last = 0;
    const pointer = { x: 0, y: 0 };
    const nodes = Array.from({ length: 96 }, (_, i) => {
      const angle = i * 2.399963;
      const radius = Math.sqrt((i + 1) / 96);
      return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius, angle, r: radius };
    });
    const draw = (time = 0) => {
      if (!visible || document.hidden) return;
      if (!reduced.matches && time - last < 32) { frame = requestAnimationFrame(draw); return; }
      last = time;
      const t = reduced.matches ? 0 : time / 4000;
      const target = modeRef.current === 'FINANCIAL' ? 1 : 0;
      blend += (target - blend) * (reduced.matches ? 1 : 0.045);
      ctx.clearRect(0, 0, width, height);
      const mobile = width < 768;
      const scale = Math.min(width * (mobile ? 0.6 : 0.28), height * 0.58);
      const cx = width * (mobile ? 0.78 : 0.74), cy = height * 0.45;
      const points = nodes.map((n, i) => {
        const ox = n.x + Math.sin(t * 0.3 + n.angle) * 0.045;
        const oy = n.y + Math.cos(t * 0.2 + n.angle) * 0.045;
        const gx = ((i % 12) / 11 - 0.5) * 1.8;
        const gy = (Math.floor(i / 12) / 7 - 0.5) * 1.5;
        return { x: cx + (ox + (gx - ox) * blend) * scale + pointer.x * n.r * 10,
          y: cy + (oy + (gy - oy) * blend) * scale * 0.82 + pointer.y * n.r * 8 };
      });
      ctx.lineWidth = 0.6;
      points.forEach((a, i) => {
        points.slice(i + 1).forEach((b, offset) => {
          const distance = Math.hypot(a.x - b.x, a.y - b.y);
          if (distance > scale * 0.32) return;
          const alpha = (1 - distance / (scale * 0.32)) * (mobile ? 0.23 : 0.55);
          ctx.strokeStyle = `rgba(45,212,191,${alpha})`;
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          if ((i + offset) % 11 === 0) {
            const p = (t * 0.2 + i * 0.17) % 1;
            ctx.fillStyle = `rgba(93,211,158,${alpha * 1.7})`;
            ctx.beginPath(); ctx.arc(a.x + (b.x - a.x) * p, a.y + (b.y - a.y) * p, 1.5, 0, Math.PI * 2); ctx.fill();
          }
        });
        ctx.fillStyle = i % 9 === 0 ? 'rgba(93,211,158,0.8)' : 'rgba(45,212,191,0.5)';
        ctx.beginPath(); ctx.arc(a.x, a.y, i % 9 === 0 ? 2.5 : 1.2, 0, Math.PI * 2); ctx.fill();
        if (i % 19 === 0) {
          ctx.strokeStyle = 'rgba(45,212,191,0.24)';
          ctx.beginPath(); ctx.arc(a.x, a.y, 8 + Math.sin(t + i) * 2, 0, Math.PI * 2); ctx.stroke();
        }
      });
      if (!reduced.matches) frame = requestAnimationFrame(draw);
    };
    const restart = () => { cancelAnimationFrame(frame); last = 0; draw(performance.now()); };
    drawRef.current = restart;
    const resize = () => {
      const rect = canvas.getBoundingClientRect(); width = rect.width; height = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr; canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); restart();
    };
    const move = (e) => { if (!reduced.matches) { pointer.x = e.clientX / window.innerWidth - 0.5; pointer.y = e.clientY / window.innerHeight - 0.5; } };
    const observer = new ResizeObserver(resize); observer.observe(canvas);
    const intersection = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; restart(); });
    intersection.observe(canvas);
    window.addEventListener('pointermove', move, { passive: true });
    document.addEventListener('visibilitychange', restart);
    reduced.addEventListener('change', restart);
    return () => {
      cancelAnimationFrame(frame); observer.disconnect(); intersection.disconnect(); drawRef.current = null;
      window.removeEventListener('pointermove', move); document.removeEventListener('visibilitychange', restart);
      reduced.removeEventListener('change', restart);
    };
  }, []);
  return <canvas ref={canvasRef} className="hero-dependency-field" data-testid="hero-dependency-field" aria-hidden="true" />;
};